import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2, XCircle, ChevronDown, X, Clock } from 'lucide-react';
import { deployments } from '../api';
import type { DeploymentJob, JobStatus } from '../types';

/**
 * Verfolgt einen angestoßenen Auftrag, bis er fertig ist.
 *
 * DAS PROBLEM, DAS ES LÖST: Ein Klick auf „Deploy" stellt den Auftrag nur in
 * die Warteschlange. Der Aufruf ist nach 200 Millisekunden zurück, die Arbeit
 * dauert eine Minute — und dazwischen zeigte die Oberfläche nichts. Wer geklickt
 * hatte, wusste nicht, ob überhaupt etwas passiert war, und klickte im Zweifel
 * noch einmal.
 *
 * DREI DINGE, DIE ES DESHALB ANDERS MACHT:
 *
 *  1. **Es verschwindet nicht von selbst.** Eine Einblendung, die nach drei
 *     Sekunden weggeht, ist bei einem Vorgang von einer Minute dasselbe wie
 *     keine Rückmeldung. Das Ergebnis bleibt stehen, bis es weggeklickt wird.
 *  2. **Es zeigt Fortschritt, nicht nur „läuft".** Die letzte Logzeile sagt,
 *     woran der Auftrag gerade ist — „Images pulled", „Starting services".
 *     Ein Drehkreis allein sagt nur, dass niemand abgestürzt ist.
 *  3. **Ein Fehlschlag bleibt lesbar.** Mit der Meldung und dem vollen Log,
 *     nicht mit „Deployment fehlgeschlagen".
 */

const ABSTAND_MS = 2000;

const LAEUFT: JobStatus[] = ['PENDING', 'RUNNING'] as JobStatus[];

function dauer(job: DeploymentJob, jetzt: number): string {
  const von = job.startedAt ? +new Date(job.startedAt) : +new Date(job.createdAt);
  const bis = job.finishedAt ? +new Date(job.finishedAt) : jetzt;
  const s = Math.max(0, Math.round((bis - von) / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

/** Die letzte Zeile, die etwas aussagt. */
function letzteZeile(logs: string | null): string | null {
  if (!logs) return null;
  const zeilen = logs
    .split('\n')
    .map((z) => z.replace(/^\[[^\]]+\]\s*/, '').trim())
    .filter((z) => z.length > 0);
  return zeilen.length ? zeilen[zeilen.length - 1].slice(0, 160) : null;
}

const TYP_TEXT: Record<string, string> = {
  CREATE_INSTANCE: 'Instanz einrichten',
  UPDATE_INSTANCE: 'Instanz aktualisieren',
  DEPLOY: 'Ausrollen',
  START_INSTANCE: 'Starten',
  STOP_INSTANCE: 'Stoppen',
  RESTART_INSTANCE: 'Neustarten',
  DELETE_INSTANCE: 'Instanz entfernen',
};

export default function JobVerlauf({
  jobId,
  onFertig,
  onSchliessen,
}: {
  jobId: string;
  /** Wird einmal aufgerufen, wenn der Auftrag durch ist — zum Nachladen. */
  onFertig?: (job: DeploymentJob) => void;
  onSchliessen: () => void;
}) {
  const qc = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [jetzt, setJetzt] = useState(() => Date.now());
  const fertigGemeldet = useRef(false);

  const { data: job } = useQuery({
    queryKey: ['deployments', jobId],
    queryFn: () => deployments.get(jobId),
    // Solange er läuft, alle zwei Sekunden. Danach nicht mehr — ein fertiger
    // Auftrag ändert sich nicht mehr, und weiterzufragen wäre Last ohne Zweck.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && LAEUFT.includes(s) ? ABSTAND_MS : false;
    },
    refetchOnWindowFocus: false,
  });

  // Eigener Takt für die Laufzeit, damit die Sekunden auch zwischen zwei
  // Abfragen weiterzählen.
  useEffect(() => {
    if (!job || !LAEUFT.includes(job.status)) return;
    const t = setInterval(() => setJetzt(Date.now()), 1000);
    return () => clearInterval(t);
  }, [job]);

  useEffect(() => {
    if (!job || LAEUFT.includes(job.status) || fertigGemeldet.current) return;
    fertigGemeldet.current = true;
    // Der Zustand der Instanz hat sich geändert — Listen und Detailansicht
    // sollen das mitbekommen, ohne dass jemand neu lädt.
    qc.invalidateQueries({ queryKey: ['instances'] });
    onFertig?.(job);
  }, [job, qc, onFertig]);

  if (!job) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-surface px-3 py-2 text-sm text-slate-500">
        <Loader2 size={14} className="animate-spin" /> Auftrag wird angelegt…
      </div>
    );
  }

  const laeuft = LAEUFT.includes(job.status);
  const gescheitert = job.status === 'FAILED';
  const zeile = letzteZeile(job.logs);

  const rahmen = gescheitert
    ? 'border-red-200 bg-red-50'
    : laeuft
      ? 'border-brand-200 bg-brand-50'
      : 'border-emerald-200 bg-emerald-50';

  return (
    <div className={`rounded-lg border ${rahmen}`}>
      <div className="flex items-start gap-2 px-3 py-2">
        <div className="mt-0.5 shrink-0">
          {laeuft ? (
            <Loader2 size={15} className="animate-spin text-brand-600" />
          ) : gescheitert ? (
            <XCircle size={15} className="text-red-600" />
          ) : (
            <CheckCircle2 size={15} className="text-emerald-600" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">
            {TYP_TEXT[job.type] ?? job.type}
            {job.status === 'PENDING' && ' — wartet auf einen freien Arbeiter'}
            {job.status === 'RUNNING' && ' läuft…'}
            {job.status === 'COMPLETED' && ' abgeschlossen'}
            {gescheitert && ' fehlgeschlagen'}
            <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-slate-500">
              <Clock size={11} /> {dauer(job, jetzt)}
            </span>
          </p>

          {gescheitert && job.errorMessage && (
            <p className="mt-1 whitespace-pre-wrap text-xs text-red-800">{job.errorMessage}</p>
          )}

          {/* Woran er gerade ist. Ein Drehkreis allein sagt nur, dass niemand
              abgestürzt ist — diese Zeile sagt, was passiert. */}
          {!gescheitert && zeile && (
            <p className="mt-0.5 truncate font-mono text-xs text-slate-500" title={zeile}>
              {zeile}
            </p>
          )}

          {job.logs && (
            <button
              onClick={() => setOffen((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-xs text-brand-700 hover:text-brand-900"
            >
              <ChevronDown size={12} className={offen ? 'rotate-180' : ''} />
              {offen ? 'Protokoll zuklappen' : `Protokoll ansehen (${job.logs.split('\n').length} Zeilen)`}
            </button>
          )}
        </div>

        {/* Erst schließbar, wenn er durch ist. Vorher wäre es ein Knopf, der
            den Eindruck erweckt, man könnte abbrechen. */}
        {!laeuft && (
          <button
            onClick={onSchliessen}
            title="Ausblenden"
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-surface hover:text-slate-600"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {offen && job.logs && (
        <pre className="max-h-80 overflow-auto border-t border-slate-200/60 bg-konsole px-3 py-2 text-[11px] leading-relaxed text-konsole-hell">
          {job.logs}
        </pre>
      )}
    </div>
  );
}
